// QnAServiceBus.jsx
import React, { useMemo, useState } from "react";
import questions from "./qnaJSONdata/QnAServiceBus.json";
import QnASideBarRight from "./QnASideBarRight";
import ScrollButton from "../../ScrollButton";

/**
 * QnAServiceBus — inline Q&A with LEFT TOC + right sidebar + keyword filter
 * - Adds Download PDF (top-right + left sidebar) and Print button
 * - Accessible Bootstrap 5 layout
 */

const SERVICEBUS_PDF = "/pdfs/servicebus-interview-questions.pdf"; // ensure file exists in /public/pdfs/
const normalize = (s = "") => s.toLowerCase();

/* ===== UI bits ===== */
function FilterBar({ value, onChange, total, shown }) {
  return (
    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
      <input
        className="form-control form-control-sm"
        style={{ maxWidth: 420 }}
        type="search"
        placeholder="Filter (e.g., queues vs topics, subscriptions, sessions, peek-lock, DLQ, ordering)…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Filter questions"
      />
      <span className="text-muted small">Showing {shown} of {total}</span>
    </div>
  );
}

function QAInlineList({ items }) {
  if (!items.length) {
    return (
      <div className="text-center text-muted py-5">
        No questions match your filter. Try a different keyword.
      </div>
    );
  }

  return (
    <ol className="ps-3">
      {items.map((it, idx) => (
        <li key={it.id ?? idx} className="mb-4">
          <h5 className="mb-2">{it.question}</h5>
          <div className="text-body" style={{ whiteSpace: "pre-wrap" }}>
            {it.answer}
          </div>
          {it.code ? (
            <pre className="mt-2 mb-0 p-3 bg-light rounded border" style={{ overflow: "auto" }}>
              <code>{it.code}</code>
            </pre>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

function OnThisPageLeft({ pdfHref }) {
  return (
    <div className="card shadow-sm border-0">
      <div className="card-body">
        <h2 className="h6 text-uppercase text-muted mb-2">On this page</h2>
        <nav className="nav flex-column small" aria-label="Section links">
          <a className="nav-link px-0" href="#servicebus">Service Bus Q&amp;A</a>
          <hr className="my-2" />
          <a
            className="nav-link px-0 text-primary d-inline-flex align-items-center"
            href={pdfHref}
            download
            aria-label="Download Service Bus interview questions PDF"
          >
            <span className="me-2" aria-hidden="true">⬇︎</span>
            Download PDF
          </a>
        </nav>
      </div>
    </div>
  );
}

function TopActions({ pdfHref }) {
  return (
    <div className="d-flex gap-2">
      <a
        href={pdfHref}
        download
        className="btn btn-outline-danger btn-sm"
        aria-label="Download Service Bus interview questions as PDF"
      >
        Download PDF
      </a>
      <button
        type="button"
        className="btn btn-outline-secondary btn-sm"
        onClick={() => window.print()}
        aria-label="Print this page"
      >
        Print
      </button>
    </div>
  );
}

/* ===== Page ===== */
export default function QnAServiceBus() {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const qn = normalize(query);
    if (!qn) return questions;
    return questions.filter((x) =>
      normalize(`${x.question} ${x.answer ?? ""} ${x.code ?? ""}`).includes(qn)
    );
  }, [query]);

  return (
    <div className="container-fluid">
      <div className="row g-4 flex-xl-nowrap">
        {/* LEFT: On This Page (sticky) */}
        <aside className="d-none d-xl-block col-xl-2 pt-3">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <OnThisPageLeft pdfHref={SERVICEBUS_PDF} />
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7 pt-3" role="main">
          <header className="mb-2 d-flex justify-content-between align-items-start">
            <div>
              <h1 className="h2 mb-1">Service Bus Interview Questions</h1>
              <p className="text-muted mb-0">
                Use the filter to quickly find topics like queues vs. topics/subscriptions, sessions &amp; ordering,
                settlement (peek-lock vs. receive-and-delete), dead-lettering, retries &amp; DLQ processing,
                scheduling, duplicate detection, and throughput/cost tuning.
              </p>
            </div>
            <TopActions pdfHref={SERVICEBUS_PDF} />
          </header>

          <FilterBar
            value={query}
            onChange={setQuery}
            total={questions.length}
            shown={filtered.length}
          />

          <section id="servicebus" className="pt-2">
            <QAInlineList items={filtered} />
          </section>

          <footer className="border-top mt-4 pt-3 small text-muted">
            Tip: In answers, mention delivery semantics (at-least-once), idempotency strategies (message-id, de-dup),
            backoff/retry, poison message handling (DLQ), session affinity, and scaling with partitions.
          </footer>

          <ScrollButton />
        </main>

        {/* RIGHT: existing sidebar */}
        <aside className="col-12 col-lg-4 col-xl-3 pt-3 border-start">
          <QnASideBarRight />
        </aside>
      </div>
    </div>
  );
}
